import { Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useMemo } from "react";

import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import type { Offer, OpenShift, OpenShiftWorkplace } from "../../Shift/Open/types";

interface FallbackShiftsListProps {
  shifts: OpenShift[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
  favoriteWorkplaceIds: Set<string>;
  onClickOpenShift: ({
    shift,
    workplace,
    offer,
  }: {
    shift: OpenShift;
    workplace: OpenShiftWorkplace;
    offer?: Offer;
  }) => void;
}

/**
 * Displays up to 5 unfiltered shifts when filters are applied but return no results
 * Shows a capped list with fade-out effect to indicate more shifts are available
 */
export function FallbackShiftsList(props: FallbackShiftsListProps) {
  const { shifts, workplacesMap, onClickOpenShift, favoriteWorkplaceIds } = props;

  // Take only the first 5
  const limitedShifts = useMemo(() => shifts.slice(0, 5), [shifts]);

  return (
    <Stack spacing={5} sx={{ py: 5 }}>
      <Title semibold variant="h5" component="h3">
        Shifts outside your filters
      </Title>

      {limitedShifts.map((shift) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        return (
          <OpenShiftCardContainer
            key={shift.id}
            shift={shift}
            workplace={workplace}
            isFavoriteWorkplace={favoriteWorkplaceIds.has(workplace.id)}
            blockingRequirementsContext={undefined}
            workers={[]}
            friendsMap={new Map()}
            recentColleaguesMap={new Map()}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer });
            }}
          />
        );
      })}

      <Text
        semibold
        variant="body2"
        color={(theme) => theme.palette.text.tertiary}
        sx={{ textAlign: "center" }}
      >
        Clear your filters to see all {shifts.length} shifts
      </Text>
    </Stack>
  );
}
